import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useOptimizelyAttributes from 'services/features/useOptimizelyAttributes';
import useSubscriptions from 'services/general/subscriptionsList';
import { getKits } from 'store/shared';
import {
  getEmail,
  getIsAuthenticated,
  getRoles,
  getUsername,
} from 'store/user';

import type { AmplitudeClientHandler } from '@repo/amplitude/client';
import { sortKitsByResultsViewableOrLatest } from '@repo/utils';
import { AnalyticsBrowser } from '@segment/analytics-next';

export const useIdentify = (
  analyticsBrowser: AnalyticsBrowser | null,
  amplitude: AmplitudeClientHandler | null,
) => {
  const isAuthenticated = useSelector(getIsAuthenticated);
  const email = useSelector(getEmail);
  const username = useSelector(getUsername);
  const roles = useSelector(getRoles);
  const kits = useSelector(getKits);
  const sortedKits = sortKitsByResultsViewableOrLatest(kits);
  const latest_results_ready = sortedKits[0]?.date_results_ready;
  const [isIdentified, setIsIdentified] = React.useState(false);
  const [isIdentifiedWithKits, setIsIdentifiedWithKits] = React.useState(false);
  const { isAnyTinyPlus } = useSubscriptions();
  const { attributes: optimizelyAttributes, isLoading: isAttributesLoading } =
    useOptimizelyAttributes();

  useEffect(() => {
    if (!analyticsBrowser && !amplitude) return;
    if (isAuthenticated && isAttributesLoading) return;

    const traitsForIdentify = {
      email,
      roles,
      hasMembership: isAnyTinyPlus,
      ...optimizelyAttributes,
    };

    const identify = (traits: Record<string, unknown>) => {
      analyticsBrowser?.identify(username, traits);
      if (username) amplitude?.identify(username, traits);
    };

    if (isAuthenticated && latest_results_ready && !isIdentifiedWithKits) {
      identify({
        ...traitsForIdentify,
        latest_results_ready, // This trait is specific to this condition
      });
      setIsIdentifiedWithKits(true);
      setIsIdentified(true);
    } else if (isAuthenticated && !isIdentified) {
      // Identify call for authenticated users, potentially without results_ready yet
      // or if the isIdentifiedWithKits path wasn't taken.
      identify(traitsForIdentify);
      setIsIdentified(true);
    } else if (!isAuthenticated && isIdentified) {
      analyticsBrowser?.reset();
      amplitude?.reset();
      setIsIdentified(false);
      setIsIdentifiedWithKits(false);
    }
  }, [
    analyticsBrowser,
    amplitude,
    email,
    isAuthenticated,
    isIdentified,
    isIdentifiedWithKits,
    latest_results_ready,
    roles,
    username,
    isAnyTinyPlus,
    optimizelyAttributes,
    isAttributesLoading,
  ]);
};
